import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { permissionsApi } from '@/services/api';
import { useAuth } from './AuthContext';

const PermissionsContext = createContext(null);

export function PermissionsProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  // Fetch permissions when user logs in
  useEffect(() => {
    if (isAuthenticated && user?.role) {
      fetchPermissions();
    } else {
      setPermissions({});
      setLoading(false);
    }
  }, [isAuthenticated, user?.role]);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      const response = await permissionsApi.getRolePermissions(user.role);
      setPermissions(response.data.permissions || {});
    } catch (error) {
      console.error('Error fetching permissions:', error);
      // Fall back to empty permissions on error
      setPermissions({});
    } finally {
      setLoading(false);
    }
  };

  // Check if user has a specific permission
  const hasPermission = useCallback((module, action) => {
    // Admins always have all permissions
    if (user?.role === 'admin') return true;
    
    const modulePerms = permissions[module] || [];
    return modulePerms.includes(action);
  }, [permissions, user?.role]);

  // Check if user has ANY of the specified permissions
  const hasAnyPermission = useCallback((module, actions) => {
    if (user?.role === 'admin') return true;
    
    const modulePerms = permissions[module] || [];
    return actions.some(action => modulePerms.includes(action));
  }, [permissions, user?.role]);

  // Check if user has ALL of the specified permissions
  const hasAllPermissions = useCallback((module, actions) => {
    if (user?.role === 'admin') return true;
    
    const modulePerms = permissions[module] || [];
    return actions.every(action => modulePerms.includes(action));
  }, [permissions, user?.role]);

  // Check if user can access a module at all (has view permission)
  const canAccessModule = useCallback((module) => {
    if (user?.role === 'admin') return true;
    
    const modulePerms = permissions[module] || [];
    return modulePerms.length > 0;
  }, [permissions, user?.role]);

  // Get all permissions for a module
  const getModulePermissions = useCallback((module) => {
    if (user?.role === 'admin') {
      return ['view', 'create', 'edit', 'delete', 'export', 'administer', 'complete_tasks', 'resolve', 'view_compensation', 'mark_complete', 'assign', 'sign'];
    }
    return permissions[module] || [];
  }, [permissions, user?.role]);

  const value = {
    permissions,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessModule,
    getModulePermissions,
    refreshPermissions: fetchPermissions
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
}

// Convenience hook for checking a single permission
export function usePermission(module, action) {
  const { hasPermission, loading } = usePermissions();
  return { allowed: hasPermission(module, action), loading };
}

// Component to conditionally render based on permissions
export function PermissionGate({ 
  module, 
  action, 
  actions, // Alternative: check if user has ANY of these actions
  allActions, // Alternative: check if user has ALL of these actions
  fallback = null, 
  children,
  showDisabled = false, // Show disabled version instead of hiding
}) {
  const { hasPermission, hasAnyPermission, hasAllPermissions, loading } = usePermissions();
  
  if (loading) return null;

  let allowed = false;
  
  if (action) {
    allowed = hasPermission(module, action);
  } else if (actions) {
    allowed = hasAnyPermission(module, actions);
  } else if (allActions) {
    allowed = hasAllPermissions(module, allActions);
  }

  if (allowed) {
    return children;
  }

  if (showDisabled && React.isValidElement(children)) {
    return React.cloneElement(children, { 
      disabled: true, 
      className: `${children.props.className || ''} opacity-50 cursor-not-allowed`,
      title: 'You do not have permission for this action'
    });
  }

  return fallback;
}

// HOC for protecting entire components/pages
export function withPermission(WrappedComponent, module, action) {
  return function PermissionProtectedComponent(props) {
    const { hasPermission, loading } = usePermissions();
    
    if (loading) {
      return (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      );
    }
    
    if (!hasPermission(module, action)) {
      return (
        <div className="flex flex-col items-center justify-center p-12 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Access Denied</h2>
          <p className="text-slate-500">You don't have permission to access this feature.</p>
          <p className="text-sm text-slate-400 mt-2">Contact your administrator if you need access.</p>
        </div>
      );
    }
    
    return <WrappedComponent {...props} />;
  };
}

export default PermissionsContext;
